import React, { useState, useEffect, useMemo } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { AccessSettings } from "../../../../../../../models/access.settings";
import { Status, TypeAccess } from "../../../../../../../util/enums";
import Input from "../../../../../../../components/Form/Input";
import Select from "../../../../../../../components/Form/Select";
import Modal from "../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  access?: AccessSettings;
  open: boolean;
  isLoading: boolean;
  hasErrors?: boolean;
  closeModal: () => void;
  submit: (access: AccessSettings) => void;
}

const AccessSettingsModal = ({ access, open, isLoading, hasErrors, closeModal, submit }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [accessSelected, setAccessSelected] = useState<AccessSettings>();
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const accessTypeList = useMemo(
    () => [
      { value: TypeAccess.DRIVEWAY, label: translate("driveway") },
      {
        value: TypeAccess.DRIVEWAY_LENGTH,
        label: translate("drivewayLength"),
      },
      { value: TypeAccess.PARKING, label: translate("parking") },
      {
        value: TypeAccess.CARRY_DISTANCE,
        label: translate("carryDistance"),
      },
      { value: TypeAccess.ACTION, label: translate("action") },
    ],
    []
  );

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    type: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const handleValidateSuccess = (access?: AccessSettings): boolean => {
    schema.validateSync(
      {
        name: access?.name,
        type: access?.type,
      },
      { abortEarly: false }
    );

    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error.inner);

    return false;
  };

  const isValid = (access?: AccessSettings) => {
    try {
      return handleValidateSuccess(access);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const clearFields = () => {
    setAccessSelected(undefined);
    setStatus(Status.INACTIVE);
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (isValid(accessSelected)) {
      if (accessSelected) submit({ ...accessSelected, status });
    }
  };

  useEffect(() => {
    if (access) {
      setAccessSelected(access);
      setStatus(access?.status || "");
    }
  }, [access]);

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      clearFields();
      closeModal();
    }
  }, [hasErrors]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid(accessSelected);
    }
  }, [hasFormBeenSubmitted, accessSelected]);

  return (
    <Modal
      data-cy="accessSettingsModal"
      open={open}
      handleClose={() => {
        clearFields();
        closeModal();
      }}
      title={access ? translate("editAccessOption") : translate("createAccessOption")}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        loading: isLoading,
        saveDisabled: isLoading,
        textSave: access ? translate("update") : translate("create"),
        dataCySave: "submitChangesButton",
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("accessStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              name="name"
              label={translate("name")}
              placeholder={translate("name")}
              value={accessSelected?.name}
              error={!!getErrorByField("name")}
              errorMessage={getErrorByField("name")}
              onChange={(event) => {
                if (!accessSelected?.id) {
                  setAccessSelected((state) =>
                    state
                      ? { ...state, name: event.target.value, value: event.target.value }
                      : ({ name: event.target.value, value: event.target.value } as AccessSettings)
                  );
                } else {
                  setAccessSelected((state) =>
                    state
                      ? { ...state, name: event.target.value }
                      : ({ name: event.target.value } as AccessSettings)
                  );
                }
              }}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Select
              name="type"
              label={translate("type")}
              value={accessSelected?.type}
              error={!!getErrorByField("type")}
              errorMessage={getErrorByField("type")}
              onChange={(event: SelectChangeEvent<any>) =>
                setAccessSelected((state) =>
                  state
                    ? { ...state, type: event.target.value }
                    : ({ type: event.target.value } as AccessSettings)
                )
              }
              data-cy="accessType"
              displayEmpty
              placeholder={translate("selectAnOption")}
            >
              {accessTypeList?.map((type) => (
                <MenuItem key={type.label} data-cy={type.value} value={type.value.toLowerCase()}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default AccessSettingsModal;
